import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, Button, Row, Col } from 'react-bootstrap';
import { Plus, X } from 'lucide-react';
import { toast } from 'react-toastify';
import { apiRequests } from '../../shared/api/apiRequests';
import { UploadIcon } from '../../features/UploadIcon/ui/UploadIcon';
import { ContentEditor } from '../../features/ContentEditor/ContentEditor.jsx';
import { LocalizationsBlock, validateLocalizations } from '../../features/LocalizationsBlock';
import { SEOMetatagsForm } from '../../shared/ui/SEOMetatagsForm';
import { FormLayout, FormSection, FormField, FormLoading } from '../../shared/ui/FormLayout';
import {
  buildEntityFormData,
  mapLocalizationsFromApi,
} from '../../shared/utils/buildEntityFormData.js';

const CONTENT_KEYS = ['innerContent'];

const LOCALIZATION_FIELDS = [
  { name: 'title', label: 'Название', type: 'text', required: true },
  {
    name: 'skills',
    label: 'Ключевые навыки',
    type: 'list',
    itemLabel: 'Навык',
    hint: 'Полный список на языке перевода',
  },
  {
    name: 'innerContent',
    label: 'Описание вакансии',
    type: 'content',
    hint: 'Пустой контент — на сайте покажется русская версия.',
  },
  { name: 'seoMetatags', label: 'SEO', type: 'seo' },
];

// Валюта у вакансии — строка
const CURRENCIES = ['USD', 'KZT', 'RUB', 'EUR', 'KGS', 'UZS', 'MNT'];

const emptyForm = {
  title: '',
  categoryId: 0,
  currency: 'USD',
  salaryFrom: '',
  salaryTo: '',
  expierence: { yearsFrom: 0, yearsTo: '' },
  imgBackgroundColorHex: '#FFFFFF',
  imgPath: '',
  skills: [],
  innerContent: { items: [] },
  seoMetatags: { title: '', description: '', keywords: '' },
};

const toNumberOrNull = (value) =>
  value === '' || value === null || value === undefined ? null : Number(value);

const getErrorMessage = (error, fallback) =>
  error?.error || error?.response?.data?.error || error?.message || fallback;

/**
 * Создание и редактирование вакансии: условия, навыки, описание, SEO и локализации — одним запросом
 */
const VacancyFormPage = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();

  const [form, setForm] = useState(null);
  const [localizations, setLocalizations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [logoImage, setLogoImage] = useState(null);
  const [skillInput, setSkillInput] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [categoriesRes, vacancy] = await Promise.all([
          apiRequests.hr.getCategories(200),
          isEdit ? apiRequests.hr.getById(id) : Promise.resolve(null),
        ]);
        const categoryItems = categoriesRes.items || [];
        setCategories(categoryItems);

        if (vacancy) {
          setForm({
            id: vacancy.id,
            title: vacancy.title || '',
            categoryId: vacancy.categoryId || vacancy.category?.id || 0,
            currency: vacancy.currency || 'USD',
            salaryFrom: vacancy.salaryFrom ?? '',
            salaryTo: vacancy.salaryTo ?? '',
            expierence: {
              id: vacancy.expierence?.id,
              yearsFrom: vacancy.expierence?.yearsFrom ?? 0,
              yearsTo: vacancy.expierence?.yearsTo ?? '',
            },
            imgBackgroundColorHex: vacancy.imgBackgroundColorHex || '#FFFFFF',
            imgPath: vacancy.imgPath || '',
            skills: (vacancy.skills || []).map((s) => ({ title: s.title || '' })),
            innerContent: { items: vacancy.innerContent?.items || [] },
            seoMetatags: vacancy.seoMetatags || emptyForm.seoMetatags,
          });
          setLocalizations(
            mapLocalizationsFromApi(vacancy.localizations, CONTENT_KEYS).map((l) => ({
              ...l,
              innerContent: l.innerContent || { items: [] },
              skills: (l.skills || []).map((s) => ({ title: s.title || '' })),
            })),
          );
        } else {
          setForm({ ...emptyForm, categoryId: categoryItems[0]?.id || 0 });
        }
      } catch (error) {
        toast.error(getErrorMessage(error, 'Не удалось загрузить данные вакансии'));
      }
    };
    load();
  }, [id]);

  const setField = (name, value) => setForm((prev) => ({ ...prev, [name]: value }));
  const setExperience = (name, value) =>
    setForm((prev) => ({ ...prev, expierence: { ...prev.expierence, [name]: value } }));

  const addSkill = () => {
    const title = skillInput.trim();
    if (!title) return;
    if (form.skills.some((s) => s.title.toLowerCase() === title.toLowerCase())) {
      setSkillInput('');
      return;
    }
    setField('skills', [...form.skills, { title }]);
    setSkillInput('');
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!form.title.trim()) {
      toast.error('Введите название вакансии');
      return;
    }
    const salaryFrom = toNumberOrNull(form.salaryFrom);
    const salaryTo = toNumberOrNull(form.salaryTo);
    if (salaryFrom !== null && salaryTo !== null && salaryTo < salaryFrom) {
      toast.error('Ставка «до» меньше ставки «от»');
      return;
    }
    const localizationsError = validateLocalizations(localizations, LOCALIZATION_FIELDS);
    if (localizationsError) {
      toast.error(localizationsError);
      return;
    }

    setSaving(true);
    try {
      const hex = form.imgBackgroundColorHex?.trim() || '';
      const model = {
        ...form,
        imgPath: undefined,
        categoryId: form.categoryId || null,
        salaryFrom,
        salaryTo,
        imgBackgroundColorHex: hex && !hex.startsWith('#') ? `#${hex}` : hex,
        expierence: {
          ...form.expierence,
          yearsFrom: toNumberOrNull(form.expierence.yearsFrom),
          yearsTo: toNumberOrNull(form.expierence.yearsTo),
        },
        skills: form.skills.filter((s) => s.title.trim()),
      };
      const preparedLocalizations = localizations.map((l) => ({
        ...l,
        innerContent: l.innerContent || { items: [] },
        skills: (l.skills || [])
          .filter((s) => s.title?.trim())
          .map((s) => ({ title: s.title.trim() })),
      }));
      const body = buildEntityFormData({
        model,
        mainImage: logoImage,
        mainImageField: 'logoImg',
        contentKeys: CONTENT_KEYS,
        localizations: preparedLocalizations,
      });

      if (isEdit) {
        await apiRequests.hr.updateVacancy(body);
        toast.success('Вакансия сохранена');
      } else {
        await apiRequests.hr.createVacancy(body);
        toast.success('Вакансия создана');
        setTimeout(() => navigate('/HR/vacancies'), 1000);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Не удалось сохранить вакансию'));
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <FormLoading />;

  const currencyOptions =
    form.currency && !CURRENCIES.includes(form.currency)
      ? [form.currency, ...CURRENCIES]
      : CURRENCIES;

  return (
    <FormLayout
      onSubmit={handleSubmit}
      onBack={() => navigate('/HR/vacancies')}
      saving={saving}
      submitText={isEdit ? 'Сохранить изменения' : 'Создать вакансию'}
      savingText={isEdit ? 'Сохранение...' : 'Создание...'}
    >
      <FormSection title="Основная информация">
        <Row>
          <Col lg={8}>
            <FormField label="Название" required>
              <Form.Control
                type="text"
                value={form.title}
                onChange={(e) => setField('title', e.target.value)}
                placeholder="Например, Senior Frontend-разработчик"
              />
            </FormField>

            <FormField label="Категория">
              <Form.Select
                value={form.categoryId || 0}
                onChange={(e) => setField('categoryId', Number(e.target.value))}
              >
                <option value={0}>Без категории</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.title}
                  </option>
                ))}
              </Form.Select>
            </FormField>

            <Row>
              <Col md={4}>
                <FormField label="Ставка от" hint="Пусто в обоих полях — «по договорённости»">
                  <Form.Control
                    type="number"
                    min={0}
                    value={form.salaryFrom}
                    onChange={(e) => setField('salaryFrom', e.target.value)}
                  />
                </FormField>
              </Col>
              <Col md={4}>
                <FormField label="Ставка до">
                  <Form.Control
                    type="number"
                    min={0}
                    value={form.salaryTo}
                    onChange={(e) => setField('salaryTo', e.target.value)}
                  />
                </FormField>
              </Col>
              <Col md={4}>
                <FormField label="Валюта">
                  <Form.Select
                    value={form.currency}
                    onChange={(e) => setField('currency', e.target.value)}
                  >
                    {currencyOptions.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </Form.Select>
                </FormField>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <FormField label="Опыт от, лет">
                  <Form.Control
                    type="number"
                    min={0}
                    value={form.expierence.yearsFrom ?? ''}
                    onChange={(e) => setExperience('yearsFrom', e.target.value)}
                  />
                </FormField>
              </Col>
              <Col md={4}>
                <FormField label="Опыт до, лет" hint="Пусто — без верхней границы">
                  <Form.Control
                    type="number"
                    min={0}
                    value={form.expierence.yearsTo ?? ''}
                    onChange={(e) => setExperience('yearsTo', e.target.value)}
                  />
                </FormField>
              </Col>
            </Row>
          </Col>

          <Col lg={4}>
            <FormField>
              <UploadIcon
                onSelect={setLogoImage}
                initialIcon={form.imgPath || null}
                entityType="vacancy"
              />
            </FormField>
            <FormField label="Цвет фона иконки">
              <div className="d-flex align-items-center">
                <Form.Control
                  type="color"
                  value={
                    /^#[0-9a-f]{6}$/i.test(form.imgBackgroundColorHex)
                      ? form.imgBackgroundColorHex
                      : '#FFFFFF'
                  }
                  onChange={(e) => setField('imgBackgroundColorHex', e.target.value)}
                  className="me-2 flex-shrink-0"
                  style={{ width: 48 }}
                />
                <Form.Control
                  type="text"
                  value={form.imgBackgroundColorHex}
                  onChange={(e) => setField('imgBackgroundColorHex', e.target.value)}
                  placeholder="#FFFFFF"
                />
              </div>
            </FormField>
          </Col>
        </Row>
      </FormSection>

      <FormSection title="Ключевые навыки">
        <div className="d-flex mb-3" style={{ maxWidth: 420 }}>
          <Form.Control
            type="text"
            value={skillInput}
            onChange={(e) => setSkillInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addSkill();
              }
            }}
            placeholder="Например, React — и Enter"
            className="me-2"
          />
          <Button variant="outline-secondary" onClick={addSkill} title="Добавить навык">
            <Plus size={16} />
          </Button>
        </div>
        {form.skills.length > 0 ? (
          <div className="d-flex flex-wrap gap-2">
            {form.skills.map((skill, index) => (
              <span
                key={`${skill.title}-${index}`}
                className="badge bg-light text-dark border d-inline-flex align-items-center p-2"
              >
                {skill.title}
                <Button
                  variant="link"
                  size="sm"
                  className="p-0 ms-2 text-secondary"
                  title="Убрать навык"
                  onClick={() =>
                    setField(
                      'skills',
                      form.skills.filter((_, i) => i !== index),
                    )
                  }
                >
                  <X size={14} />
                </Button>
              </span>
            ))}
          </div>
        ) : (
          <Form.Text className="text-muted">
            Навыки показываются тегами на странице вакансии и помогают в поиске.
          </Form.Text>
        )}
      </FormSection>

      <FormSection title="Описание вакансии">
        <ContentEditor
          key={form.id || 'new'}
          value={form.innerContent.items}
          onChange={(items) => setField('innerContent', { items })}
        />
      </FormSection>

      <FormSection title="SEO">
        <SEOMetatagsForm
          seoMetatags={form.seoMetatags}
          onChange={(seo) => setField('seoMetatags', seo)}
        />
      </FormSection>

      <LocalizationsBlock
        value={localizations}
        onChange={setLocalizations}
        fields={LOCALIZATION_FIELDS}
        source={form}
        aiContext="вакансия"
      />
    </FormLayout>
  );
};

export default VacancyFormPage;
